/**
 * BuilderSidebar - Block palette and properties panel
 * Blocks are dragged from here to the canvas columns
 */

import React, { useState } from 'react';
import { RowLayout } from './types';
import { BlockPalette } from './BlockPalette';
import { PropertiesPanel } from './PropertiesPanel';
import { EmailBuilderState, EmailBuilderActions } from './useEmailBuilder';
import { LayoutGrid, Settings } from 'lucide-react';

interface BuilderSidebarProps {
  state: EmailBuilderState;
  actions: EmailBuilderActions;
}

type TabType = 'content' | 'settings';

export const BuilderSidebar: React.FC<BuilderSidebarProps> = ({
  state,
  actions,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('content');

  // Only show Settings tab if there are actual content blocks (not just empty rows)
  const hasContentBlocks = state.document.rows.some(row => 
    row.columns.some(col => col.blocks.length > 0)
  );

  return (
    <div className="w-72 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('content')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            activeTab === 'content'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <LayoutGrid className="w-4 h-4" />
          Content
        </button>
        {hasContentBlocks && (
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'content' || !hasContentBlocks ? (
          <div className="p-4 space-y-6">
            {/* Row Layouts */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Rows
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {(['1-col', '2-col', '3-col'] as RowLayout[]).map((layout) => (
                  <button
                    key={layout}
                    onClick={() => actions.addRow(layout)}
                    className="flex flex-col items-center gap-1 p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <RowLayoutIcon layout={layout} />
                    <span className="text-xs text-gray-500">{layout}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Blocks - Drag to canvas */}
            <BlockPalette />
          </div>
        ) : (
          <PropertiesPanel
            state={state}
            actions={actions}
          />
        )}
      </div>
    </div>
  );
};

// Row layout icon component
const RowLayoutIcon: React.FC<{ layout: RowLayout }> = ({ layout }) => {
  // For 1-col, show a single solid rectangle
  if (layout === '1-col') {
    return (
      <div className="w-8 h-5 bg-gray-300 rounded-sm" />
    );
  }

  const getColumns = () => {
    switch (layout) {
      case '2-col':
        return [50, 50];
      case '3-col':
        return [33, 34, 33];
      case '1-2':
        return [33, 67];
      case '2-1':
        return [67, 33];
      default:
        return [100];
    }
  };

  return (
    <div className="flex gap-0.5 w-8 h-5">
      {getColumns().map((width, i) => (
        <div
          key={i}
          className="bg-gray-300 rounded-sm"
          style={{ width: `${width}%`, height: '100%' }}
        />
      ))}
    </div>
  );
};

